import type { IDBPDatabase } from "idb";

import { useEffect, useState } from "react";

import { openDB } from "idb";

interface HtmlCache {
  projectId: string;
  pageRoute: string;
  html: string;
  timestamp: number;
}
interface CSSCache {
  projectId: string;
  css: string;
  timestamp: number;
}

const DB_NAME = "PageCache";
const DB_VERSION = 1; // Increment to trigger upgrade
const HTML_STORE_NAME = "html-cache";
const CSS_STORE_NAME = "css-cache";
const CACHE_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * This class is used to manage the Page Cache. HTML & CSS are cached separately
 * because all pages for a project share the same CSS, and the CSS is quite
 * large.
 */
class PageCacheManager {
  private db: Promise<IDBPDatabase>;

  constructor() {
    this.db = this.init();
  }

  async init(): Promise<IDBPDatabase> {
    try {
      const db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(HTML_STORE_NAME)) {
            const store = db.createObjectStore(HTML_STORE_NAME, {
              keyPath: ["projectId", "pageRoute"],
            });
            // Create an index for projectId to make clearProject more efficient
            store.createIndex("by-project", "projectId");
          }
          if (!db.objectStoreNames.contains(CSS_STORE_NAME)) {
            db.createObjectStore(CSS_STORE_NAME, {
              keyPath: ["projectId"],
            });
          }
        },
      });
      return db;
    } catch (error) {
      console.error("Failed to initialize database:", error);
      throw error;
    }
  }

  async saveHtml({
    projectId,
    pageRoute,
    html,
  }: {
    projectId: string;
    pageRoute: string;
    html: string;
  }): Promise<void> {
    try {
      const db = await this.db;

      const cache: HtmlCache = {
        projectId,
        pageRoute,
        html,
        timestamp: Date.now(),
      };

      await db.put(HTML_STORE_NAME, cache);
    } catch (error) {
      console.error("Failed to save HTML:", error);
      throw error;
    }
  }

  async getHtml({
    projectId,
    pageRoute,
  }: {
    projectId: string;
    pageRoute: string;
  }): Promise<string | null> {
    try {
      const db = await this.db;
      const cache = await db.get(HTML_STORE_NAME, [projectId, pageRoute]);

      if (!cache) {
        return null;
      }

      // Check if cache is still valid
      const age = Date.now() - cache.timestamp;
      if (age > CACHE_AGE_MS) {
        return null;
      }

      return cache.html;
    } catch (error) {
      console.error("Failed to get HTML:", error);
      return null;
    }
  }

  async clearHtml({
    projectId,
    pageRoute,
  }: {
    projectId: string;
    pageRoute: string;
  }): Promise<void> {
    try {
      const db = await this.db;
      await db.delete(HTML_STORE_NAME, [projectId, pageRoute]);
    } catch (error) {
      console.error("Failed to clear HTML:", error);
      throw error;
    }
  }

  async saveCss({
    projectId,
    css,
  }: {
    projectId: string;
    css: string;
  }): Promise<void> {
    try {
      const db = await this.db;
      const cache: CSSCache = {
        projectId,
        css,
        timestamp: Date.now(),
      };
      await db.put(CSS_STORE_NAME, cache);
    } catch (error) {
      console.error("Failed to save CSS:", error);
      throw error;
    }
  }

  async getCss({ projectId }: { projectId: string }): Promise<string | null> {
    try {
      const db = await this.db;
      const cache = await db.get(CSS_STORE_NAME, [projectId]);

      if (!cache) {
        return null;
      }

      // Check if cache is still valid
      const age = Date.now() - cache.timestamp;
      if (age > CACHE_AGE_MS) {
        return null;
      }

      return cache.css;
    } catch (error) {
      console.error("Failed to get CSS:", error);
      return null;
    }
  }

  async clearCss({ projectId }: { projectId: string }): Promise<void> {
    try {
      const db = await this.db;
      await db.delete(CSS_STORE_NAME, projectId);
    } catch (error) {
      console.error("Failed to clear CSS:", error);
      throw error;
    }
  }

  async clearProjectHtml(projectId: string): Promise<void> {
    try {
      const db = await this.db;
      const tx = db.transaction(HTML_STORE_NAME, "readwrite");
      const store = tx.objectStore(HTML_STORE_NAME);
      const index = store.index("by-project");

      // Get all keys for this project
      const keys = await index.getAllKeys(projectId);

      // Delete all entries for this project
      await Promise.all(keys.map((key) => store.delete(key)));

      await tx.done;
    } catch (error) {
      console.error("Failed to clear project cache:", error);
      throw error;
    }
  }

  async clearProjectCss(projectId: string): Promise<void> {
    try {
      const db = await this.db;
      const tx = db.transaction(CSS_STORE_NAME, "readwrite");
      const store = tx.objectStore(CSS_STORE_NAME);
      await store.delete(projectId);
      await tx.done;
    } catch (error) {
      console.error("Failed to clear project cache:", error);
      throw error;
    }
  }

  async clearProject(projectId: string): Promise<void> {
    await Promise.all([
      this.clearProjectHtml(projectId),
      this.clearProjectCss(projectId),
    ]);
  }

  async clearAll(): Promise<void> {
    try {
      const db = await this.db;
      await db.clear(HTML_STORE_NAME);
      await db.clear(CSS_STORE_NAME);
    } catch (error) {
      console.error("Failed to clear all cache:", error);
      throw error;
    }
  }
}

const pageCacheManager = new PageCacheManager();

export const useSaveHtmlContent = ({ projectId }: { projectId?: string }) => {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!projectId) {
        return;
      }

      if (event.data.type === "emitHTML") {
        void pageCacheManager.saveCss({
          projectId,
          css: event.data.cssContent,
        });

        void pageCacheManager.saveHtml({
          projectId,
          pageRoute: event.data.pageRoute,
          html: event.data.htmlContent,
        });
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, [projectId]);
};

// Note (Ben O., 07/11/2025): This is a utility function to transform the
// Next.js image component to a regular img component. This is necessary
// because the Next.js image component uses a relative path to the image,
// which is not supported in html previews.
function transformNextImageToImg(html: string): string {
  return html.replace(
    /<img([^>]+)src=["']\/_next\/image\?([^"']+)["']([^>]*)>/gi,
    (match, beforeSrc, params, afterSrc) => {
      const searchParams = new URLSearchParams(params.replace(/&amp;/g, "&"));
      const originalUrl = searchParams.get("url")
        ? decodeURIComponent(searchParams.get("url")!)
        : null;

      if (!originalUrl) {
        return match;
      }

      const cleaned = (beforeSrc + afterSrc)
        .replace(/srcset=["'][^"']*["']/gi, "")
        .replace(/\s{2,}/g, " ");

      return `<img${cleaned}src="${originalUrl}">`;
    },
  );
}

/**
 * This is a hook that can be used to get the static html content for a page.
 * Data will only be returned if this page has previously been rendered on this
 * client.
 */
export const useCachedPageContent = ({
  pageRoute,
  projectId,
}: {
  pageRoute: string;
  projectId?: string;
}): string | undefined => {
  const [html, setHtml] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const getHtml = async () => {
      const [css, html] = await Promise.all([
        pageCacheManager.getCss({ projectId }),
        pageCacheManager.getHtml({ projectId, pageRoute }),
      ]);

      if (css && html) {
        let transformHtml = html.replace(
          /<link rel="stylesheet" href="([^"]+)"[^>]*>/,
          `<style>${css}</style>`,
        );

        transformHtml = transformNextImageToImg(transformHtml);

        setHtml(transformHtml);
      } else {
        setHtml(html ?? undefined);
      }
    };

    void getHtml();
  }, [pageRoute, projectId]);

  return html;
};
